import { create } from "zustand";
import { QuickTradeNotificationData, ParsedChatJson, ParsedOrderJson } from "@sdk/notification/types";

export type NotificationPermission = "default" | "granted" | "denied";

export interface NotificationItem {
  id: string;
  type: "order" | "system" | "cancelled";
  title: string;
  message: string;
  timestamp: Date;
  orderId?: string;
  buyerUsername?: string;
  amount?: {
    value: number;
    currency: string;
  };
  fiatAmount?: {
    value: number;
    currency: string;
  };
  userAvatar?: string;
  orderType?: "buy" | "sell";
  isRead?: boolean;
  state?: string;
}

interface NotificationStore {
  orderNotifications: NotificationItem[];
  chatNotification: ParsedChatJson[];
  quickTradeNotification: QuickTradeNotificationData | null;
  setQuickTradeNotification: (payload: QuickTradeNotificationData | null) => void;
  addChatNotification: (message: ParsedChatJson | ParsedChatJson[]) => Promise<void>;
  updateChatNotification: (id: number, updated: ParsedChatJson) => void;
  addOrderNotification: (payload: ParsedOrderJson | null) => Promise<void>;
  hasNotification: boolean;
  permissionStatus: NotificationPermission;
  setPermissionStatus: (status: NotificationPermission) => void;
  removeOrderNotificationsByIds: (ids: string[]) => void;
  setIsWebsocketActive: (active: boolean) => void;
  isWebsocketActive: boolean;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  quickTradeNotification: null,
  setQuickTradeNotification: (payload: QuickTradeNotificationData | null) => set({ quickTradeNotification: payload }),
  orderNotifications: [],
  chatNotification: [],
  hasNotification: false,
  permissionStatus: "default",
  isWebsocketActive: false,

  setPermissionStatus: status => set({ permissionStatus: status }),
  setIsWebsocketActive: (active: boolean) => set({ isWebsocketActive: active }),

  addChatNotification: async (message: ParsedChatJson | ParsedChatJson[]) => {
    if (!message || (Array.isArray(message) && message.length === 0)) return;

    if (Array.isArray(message)) {
      const targetOrderId = message[0].orderId;

      set(state => ({
        chatNotification: [
          ...state.chatNotification.filter(m => m.orderId !== targetOrderId),
          ...message.filter(m => m.orderId === targetOrderId),
        ],
      }));
    } else {
      set(state => ({
        chatNotification: [...state.chatNotification, message],
      }));
    }
  },

  updateChatNotification: (id, updated) => {
    set(state => ({
      chatNotification: state.chatNotification.map(msg => (msg.id === id ? updated : msg)),
    }));
  },

  addOrderNotification: async (payload: ParsedOrderJson | null) => {
    if (!payload) return;

    const title = payload.title?.toLowerCase() || "";
    const type = title.includes("buy") || title.includes("sell") ? "order" : "system";
    const ordType = title.includes("buy") ? "buy" : title.includes("sell") ? "sell" : undefined;

    const newNotification: NotificationItem = {
      id: payload.id,
      orderId: payload.id,
      type,
      title: payload.title || "",
      message: payload.body || "",
      timestamp: payload.createdAt,
      orderType: ordType,
      isRead: payload.red,
      state: payload.state,
    };

    set(state => ({
      orderNotifications: [...state.orderNotifications, newNotification],
      hasNotification: true,
    }));
  },

  removeOrderNotificationsByIds: ids =>
    set(state => ({
      orderNotifications: state.orderNotifications.filter(n => !ids.includes(n.id)),
    })),
}));
